/*
  Golden Lighting Wizard — shared standalone runtime
  drafted 2026-07-07

  WHAT THIS FILE IS: the generic UI shell that walks ANY topic module
  matching the shape { steps, entryStep, recommend(state) } (see
  gl-ceiling-engine.js for the reference implementation of that shape).
  This file has ZERO topic-specific content — no rule text, no thresholds.
  That separation is the point: this same runtime renders the ceiling-height
  topic today and will render chandelier-sizing/vanity-lighting/etc. topics
  later with no changes needed here.

  USE CASE: standalone, single-topic embeds on SEO pillar pages (one topic
  per embed, e.g. this file + gl-ceiling-engine.js on the "How to Light Low
  Ceilings" article). This is NOT the eventual multi-topic "Product Genius"
  orchestrator — that will likely want its own combined-flow runtime that
  also imports each topic's engine file for its RULES/recommend logic, but
  drives a longer combined question sequence across topics rather than
  mounting N separate single-topic widgets. Keeping topic logic here in pure,
  render-agnostic engine files (see gl-ceiling-engine.js) means that future
  orchestrator can reuse the same engines without anyone rewriting the rule
  logic a second time.

  DEPLOY AS A THEME ASSET alongside each topic's engine file. Shopify admin
  -> Online Store -> Themes -> Edit code -> Assets -> Add a new asset ->
  upload as "gl-wizard-runtime.js".

  TELEMETRY (Yuh-Mei 2026-07-07 strategy: market each topic as its own
  individually-branded "genius" - single-purpose tools read as more
  trustworthy/expert than one generic do-everything assistant - but feed
  every genius's engagement data into ONE central learning system, "one ring
  that rules them all"). This runtime dispatches a single decoupled
  `glwizard:event` CustomEvent on `document` for every interaction (question
  answered, result shown, CTA/guide link clicked, restart). It does NOT know
  or care which analytics backend is listening - GA4, Klaviyo, a future
  in-house aggregator, whatever gets chosen. Wire up a listener once, on any
  page, and it captures every genius's activity with zero changes to this
  file or any engine file:
    document.addEventListener('glwizard:event', function (e) {
      console.log(e.detail); // {topic, type, ...fields, ts}
    });
  Event `type` values: "start", "answer" (fields: step, field, value, label),
  "result" (fields: title), "cta_click" (fields: label, href),
  "secondary_click" (fields: label, href), "restart".
*/
(function (global) {
  "use strict";
  var GLWizardRuntime = global.GLWizardRuntime = global.GLWizardRuntime || {};

  var STYLE_ID = "glw-runtime-style";
  var CSS = "\
    .glw-root { --glw-accent:#a9822f; --glw-ink:#211d16; --glw-muted:#6b6355; --glw-bg:#faf8f4; --glw-card:#ffffff; --glw-border:#e4ddcf; --glw-radius:14px; max-width:480px; margin:0 auto; font-family:Georgia,'Times New Roman',serif; color:var(--glw-ink); box-sizing:border-box; }\
    .glw-root *, .glw-root *::before, .glw-root *::after { box-sizing:border-box; }\
    .glw-card { background:var(--glw-card); border:1px solid var(--glw-border); border-radius:var(--glw-radius); padding:28px 24px; }\
    .glw-eyebrow { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; text-transform:uppercase; letter-spacing:.08em; font-size:12px; color:var(--glw-accent); margin:0 0 6px; }\
    .glw-root h3 { margin:0 0 18px; font-size:22px; line-height:1.3; }\
    .glw-options { display:flex; flex-direction:column; gap:10px; }\
    .glw-root button.glw-opt { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; text-align:left; background:var(--glw-bg); border:1px solid var(--glw-border); border-radius:10px; padding:14px 16px; font-size:15px; color:var(--glw-ink); cursor:pointer; transition:border-color .15s ease,background .15s ease; }\
    .glw-root button.glw-opt:hover { border-color:var(--glw-accent); background:#fff; }\
    .glw-sub { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; font-size:14px; color:var(--glw-muted); margin:-10px 0 18px; }\
    .glw-result p { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; font-size:15px; line-height:1.6; color:var(--glw-ink); }\
    .glw-cite { font-size:12px; color:var(--glw-muted); margin-top:16px; }\
    .glw-cta { display:inline-block; margin-top:14px; background:var(--glw-accent); color:#fff; text-decoration:none; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; font-size:14px; font-weight:600; padding:12px 20px; border-radius:8px; margin-right:10px; }\
    .glw-link { display:inline-block; margin-top:14px; color:var(--glw-accent); font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; font-size:14px; text-decoration:underline; }\
    .glw-restart { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; background:none; border:none; color:var(--glw-muted); font-size:13px; text-decoration:underline; cursor:pointer; padding:0; margin-top:20px; display:block; }\
  ";

  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  /**
   * Mount a single-topic wizard into `root` (a DOM node).
   * `topic` must match { steps, entryStep, recommend(state), rules, label }.
   */
  function emit(topic, type, fields) {
    var detail = Object.assign({ topic: topic.id, type: type, ts: Date.now() }, fields || {});
    document.dispatchEvent(new CustomEvent("glwizard:event", { detail: detail, bubbles: true }));
  }

  GLWizardRuntime.mount = function (root, topic) {
    ensureStyle();
    root.classList.add("glw-root");
    var state = {};

    function clear() { root.innerHTML = ""; }

    function restartBtn() {
      var b = el("button", "glw-restart", "Start over");
      b.type = "button";
      b.addEventListener("click", function () { start(true); });
      return b;
    }

    function renderStep(stepId) {
      if (stepId === "result") { renderResult(); return; }
      var step = topic.steps[stepId];
      clear();
      var card = el("div", "glw-card");
      if (stepId === topic.entryStep) {
        card.appendChild(el("p", "glw-eyebrow", "Fixture Sizing Helper"));
      }
      card.appendChild(el("h3", null, step.prompt));
      var opts = el("div", "glw-options");
      step.options.forEach(function (opt) {
        var b = el("button", "glw-opt", opt.label);
        b.type = "button";
        b.addEventListener("click", function () {
          state[step.field] = opt.value;
          emit(topic, "answer", { step: step.id, field: step.field, value: opt.value, label: opt.label });
          renderStep(opt.next);
        });
        opts.appendChild(b);
      });
      card.appendChild(opts);
      if (step.helpLink) {
        var h = el("button", "glw-opt", step.helpLink.label);
        h.type = "button";
        h.addEventListener("click", function () { renderHelp(step.helpLink); });
        opts.appendChild(h);
      }
      card.appendChild(restartBtn());
      root.appendChild(card);
    }

    function renderHelp(help) {
      clear();
      var card = el("div", "glw-card");
      card.appendChild(el("h3", null, help.prompt));
      card.appendChild(el("p", "glw-sub", help.text));
      card.appendChild(restartBtn());
      root.appendChild(card);
    }

    function renderResult() {
      clear();
      var r = topic.recommend(state);
      emit(topic, "result", { title: r.title, state: Object.assign({}, state) });
      var card = el("div", "glw-card");
      card.appendChild(el("h3", null, r.title));
      var body = el("div", "glw-result");
      r.paragraphs.forEach(function (p) { body.appendChild(el("p", null, p)); });
      var cite = el("p", "glw-cite", r.citation);
      body.appendChild(cite);
      if (r.cta) {
        var cta = document.createElement("a");
        cta.className = "glw-cta";
        cta.href = r.cta.href;
        cta.textContent = r.cta.label;
        cta.addEventListener("click", function () {
          emit(topic, "cta_click", { label: r.cta.label, href: r.cta.href });
        });
        body.appendChild(cta);
      }
      if (r.secondaryLink) {
        var link = document.createElement("a");
        link.className = "glw-link";
        link.href = r.secondaryLink.href;
        link.textContent = r.secondaryLink.label;
        link.addEventListener("click", function () {
          emit(topic, "secondary_click", { label: r.secondaryLink.label, href: r.secondaryLink.href });
        });
        body.appendChild(link);
      }
      card.appendChild(body);
      card.appendChild(restartBtn());
      root.appendChild(card);
    }

    function start(isRestart) {
      state = {};
      emit(topic, isRestart ? "restart" : "start", {});
      renderStep(topic.entryStep);
    }
    start(false);
  };
})(window);
